"use client";
import Image from "next/image";
import Button from "./Button";
import SectionTitle from "./SectionTitle";

type MaxWidth =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "full";

type SectionTitleInputProps = {
  title?: string;
  description?: string | string[];
  align?: "left" | "center" | "right";
  titleMaxWidth?: MaxWidth;
  descriptionMaxWidth?: MaxWidth;
};

type IndustriesProps = {
  buttonLabel?: string;
  buttonLink?: string;
  buttonOnClick?: () => void;
  sectionTitle?: SectionTitleInputProps;
};

export default function BDLCSection({
  buttonLabel,
  buttonOnClick,
  sectionTitle,
}: IndustriesProps) {
  const steps = [
    {
      title: "Technology & SaaS",
      iconSrc: "/icons/Section-Icons/organic-visibility/TechnologySaaS.svg",
    },
    {
      title: "Healthcare & Wellness",
      iconSrc:
        "/icons/Section-Icons/organic-visibility/HealthCareWellness.svg",
    },
    {
      title: "Real Estate & Construction",
      iconSrc:
        "/icons/Section-Icons/organic-visibility/RealEstateConstruction.svg",
    },
    {
      title: "Education & EdTech",
      iconSrc: "/icons/Section-Icons/organic-visibility/EducationEdTech.svg",
    },
    {
      title: "Legal & Professional Services",
      iconSrc:
        "/icons/Section-Icons/organic-visibility/LegalProfessionalServices.svg",
    },
    {
      title: "Finance & Consulting",
      iconSrc: "/icons/Section-Icons/organic-visibility/FinanceConsulting.svg",
    },
    {
      title: "E-commerce & Retail",
      iconSrc: "/icons/Section-Icons/organic-visibility/EcommerceRetail.svg",
    },
    {
      title: "Manufacturing & B2B",
      iconSrc: "/icons/Section-Icons/organic-visibility/ManufacturingB2B.svg",
    },
    {
      title: "Hospitality & Travel",
      iconSrc: "/icons/Section-Icons/organic-visibility/Hospitality.svg",
    },
    {
      title: "Restaurant",
      iconSrc: "/icons/Section-Icons/organic-visibility/Restaurant.svg",
    },
  ];

  return (
    <>
      {/* Desktop layout */}
      <section className="relative overflow-hidden hidden section-bg-white 2xl:block">
        <div className="custom-container">
          <div className="grid grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="col-span-7 max-w-xl z-50">
              <SectionTitle
                title="Industries We’ve Served"
                description={
                  sectionTitle?.description ??
                  "Organic visibility challenges differ across sectors, but our result-oriented frameworks adapt to each industry’s growth dynamics. By embedding our BDLC methodology and visibility systems, we’ve enabled brands to scale, capture market share, and drive measurable ROI across diverse verticals."
                }
                align={sectionTitle?.align ?? "left"}
                titleMaxWidth={sectionTitle?.titleMaxWidth ?? "sm"}
                descriptionMaxWidth={
                  sectionTitle?.descriptionMaxWidth ?? "lg"
                }
              />

              {buttonLabel && (
                <div className="mt-12">
                  <Button
                    responsiveSize={{
                      mobile: "sm",
                      tablet: "md",
                      desktop: "lg",
                    }}
                    className="text-base sm:text-lg md:text-xl"
                    {...(buttonOnClick && { onClick: buttonOnClick })}
                    href={"/contact-us"}
                  >
                    {buttonLabel}
                  </Button>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="col-span-5 relative">
              <div className="flex flex-col relative transform -skew-x-24 py-12">
                <div className="absolute left-24 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                {steps.map((step, i) => (
                  <div
                    key={i}
                    className="flex mb-10 items-center relative gap-6 transform skew-x-24 group"
                  >
                    {/* Icon */}
                    <div className="w-12 h-12">
                      <Image
                        src={step.iconSrc}
                        alt={`${step.title} icon`}
                        width={48}
                        height={48}
                        className="w-12 h-12 filter grayscale brightness-90 transition duration-300"
                      />
                    </div>

                    {/* Circle */}
                    <div className="flex flex-col items-center">
                      <div className="relative size-12 rounded-full bg-white group-hover:border-2 border-[#d53333] shadow-[0_4px_74px_0_rgba(48,93,207,0.20)] flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                        <div className="w-3 h-3 rounded-full bg-red-600"></div>
                      </div>

                      {i < steps.length - 1 && (
                        <div className="flex-1 w-0.5 bg-gray-300 mt-1"></div>
                      )}
                    </div>

                    {/* Text */}
                    <h4 className="font-bold text-[#1b1b1b] text-lg leading-snug transition-colors duration-300 group-hover:text-[#d53333]">
                      {step.title}
                    </h4>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile version */}
      <div className="2xl:hidden px-4 overflow-hidden section-background-filler py-16">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-[#272d57]">
            {sectionTitle?.title ?? "Industries We’ve Served"}
          </h2>
          <p className="text-base text-[#1b1b1b] mt-2">
            {sectionTitle?.description ??
              "Organic visibility challenges differ across sectors, but our result-oriented frameworks adapt to each industry’s growth dynamics."}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {steps.map((step, i) => (
            <div
              key={i}
              className="flex flex-col items-center bg-white p-4 border border-gray-200 transition duration-300 group"
            >
              <div className="w-12 h-12 mb-2">
                <Image
                  src={step.iconSrc}
                  alt={step.title}
                  width={48}
                  height={48}
                  className="w-12 h-12 filter grayscale brightness-90  transition duration-300"
                />
              </div>

              <h4 className="text-center font-semibold text-sm group-hover:text-[#d53333]">
                {step.title}
              </h4>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        {buttonLabel && (
          <div className="mt-8 text-center">
            <Button
              responsiveSize={{ mobile: "md", tablet: "lg" }}
              {...(buttonOnClick && { onClick: buttonOnClick })}
              href={"/contact-us"}
            >
              {buttonLabel}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}